/**
 * QA-79: the command that makes a fresh owner sign-in link for one hub (GET /api/signin-hint).
 * A plain `orgus token` targets the default Orgus home, so a hub started with another
 * ORGUS_HOME (the follow-main hub on 4600) refuses its link. The hint names the hub's own home.
 *
 * It carries no secret: only a folder path and the command. A hub listening beyond loopback
 * gives no path (`commands: null`): its home is nobody else's business. The control plane
 * signs in through GitHub and has no such route.
 */

export interface SigninHintCommands {
  powershell: string;
  bash: string;
}

export interface SigninHint {
  /** Null when the hub listens beyond this computer: no path is shown. */
  commands: SigninHintCommands | null;
}

/** Today's words, for a hub that gives no hint (older, unreachable, or not on loopback). */
export const SIGNIN_HINT_FALLBACK = 'orgus token';

const psQuote = (s: string): string => `'${s.replace(/'/g, "''")}'`;
const shQuote = (s: string): string => `'${s.replace(/'/g, `'\\''`)}'`;

/** The exact `orgus token` invocation for a hub whose home is `home`, or no path for a non-loopback hub. */
export function signinHint(home: string, loopback: boolean): SigninHint {
  if (!loopback || !home) return { commands: null };
  return {
    commands: {
      powershell: `$env:ORGUS_HOME=${psQuote(home)}; orgus token`,
      bash: `ORGUS_HOME=${shQuote(home)} orgus token`,
    },
  };
}

/** A hint from the wire, checked: anything malformed counts as no hint. */
export function parseSigninHint(json: unknown): SigninHint {
  const c = (json as { commands?: unknown } | null)?.commands as Partial<SigninHintCommands> | null | undefined;
  if (c && typeof c.powershell === 'string' && typeof c.bash === 'string') return { commands: { powershell: c.powershell, bash: c.bash } };
  return { commands: null };
}
